/* eslint-disable max-lines */
import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MaybeTooltip } from '@modernloop/shared/components';
import {
  CrossIcon,
  DiamondFilled,
  EditIcon,
  FilterIcon,
  ModulesIcon,
  ReverseShadowIcon,
  ShadowIcon,
  TraineeIcon,
} from '@modernloop/shared/icons';
import { Avatar, Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import { ModuleSeatV2_SeatFragment, TrainingStatus, useModuleMemberStatsV2Query } from 'src/generated/mloop-graphql';

import {
  AttributeMap,
  TRAINEE_REQUIREMENT,
  TRAINEE_SCHEDULING_STRATEGY,
} from 'src/store/slices/job-stage-interview-seat';

import {
  filterModuleMembersBySeatAttributes,
  hasTraineeAttributeFilters,
  resolveInTrainingAttributeMap,
  toRoleAttributeMap,
} from './memberFiltering';
import { isReverseShadowRequirementEnabled, isShadowRequirementEnabled } from './SeatSettings/seatSettingsUtils';
import { useAvatarSxProps, useGridSxProps, useSxProps } from './sxProps';

const MAX_NAMES_PER_TOOLTIP = 3;

type TooltipMember = { employeeId: string; fullName: string };

interface Props {
  rowRef: React.RefObject<HTMLDivElement>;
  handleMouseOut: () => void;
  handleMouseOver: () => void;
  seatIndex: number;
  onEdit: () => void;
  showOptions: boolean;
  onDelete: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface Fragments {
  seat: ModuleSeatV2_SeatFragment;
}

/**
 * Collapsed module seat card shown when the trainee_attribute_filtering flag is ON.
 *
 * Compared to the legacy ModuleSeat: filtering moves into the inline edit-mode settings panel (no
 * hover filter button / popover), and the trained / reverse shadow / shadow counts collapse into
 * icon + count segments whose details live in per-segment tooltips. Counts reflect the per-role
 * attribute filters, computed client-side over the module's members.
 */
const ModuleSeatV2: FCWithFragments<Fragments, Props> = ({
  seat,
  rowRef,
  handleMouseOut,
  handleMouseOver,
  seatIndex,
  onEdit,
  showOptions,
  onDelete,
}) => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const gridSxProps = useGridSxProps();
  const avatarSxProps = useAvatarSxProps();
  const interviewModule = seat?.moduleSeat?.interviewModule;

  const attributeMap = useMemo<AttributeMap>(() => {
    const trainedSelectedAttributes = seat?.moduleSeat?.trainedConfig?.selectedAttributes;
    // An empty (not just absent) trained config attribute list still falls back to the legacy
    // jobStageInterviewSeatAttributes, otherwise saved legacy filters are silently dropped.
    return (
      toRoleAttributeMap(
        trainedSelectedAttributes?.length
          ? trainedSelectedAttributes
          : seat?.moduleSeat?.jobStageInterviewSeatAttributes
      ) ?? {}
    );
  }, [seat?.moduleSeat?.trainedConfig?.selectedAttributes, seat?.moduleSeat?.jobStageInterviewSeatAttributes]);
  const shadowAttributeMap = useMemo<AttributeMap | undefined>(
    () => toRoleAttributeMap(seat?.moduleSeat?.shadowConfig?.selectedAttributes),
    [seat?.moduleSeat?.shadowConfig?.selectedAttributes]
  );
  const reverseShadowAttributeMap = useMemo<AttributeMap | undefined>(
    () => toRoleAttributeMap(seat?.moduleSeat?.reverseShadowConfig?.selectedAttributes),
    [seat?.moduleSeat?.reverseShadowConfig?.selectedAttributes]
  );

  const traineeRequirement = seat?.moduleSeat?.traineeRequirement ?? TRAINEE_REQUIREMENT.ANY;
  const showReverseShadowTrainee = isReverseShadowRequirementEnabled(traineeRequirement);
  const showShadowTrainee = isShadowRequirementEnabled(traineeRequirement);
  const traineeSchedulingStrategy =
    seat?.moduleSeat?.traineeSchedulingStrategy ?? TRAINEE_SCHEDULING_STRATEGY.IS_AVAILABLE;

  // Module-wide stats are the count source when no filters are configured.
  const { data: statsData, loading: statsLoading } = useModuleMemberStatsV2Query();

  const allInterviewModuleMembers = useMemo(
    () => interviewModule?.interviewModuleMembers?.items?.filter(Boolean) || [],
    [interviewModule?.interviewModuleMembers?.items]
  );

  const filteredMembers = useMemo(
    () =>
      filterModuleMembersBySeatAttributes({
        interviewModuleMembers: allInterviewModuleMembers,
        attributeMap,
        shadowAttributeMap,
        reverseShadowAttributeMap,
      }),
    [allInterviewModuleMembers, attributeMap, reverseShadowAttributeMap, shadowAttributeMap]
  );

  // Mirror seatSettingsAdapters: use the per-role config selection when present, otherwise fall back
  // to the legacy flat selectedEmployeeIds list partitioned by member status. Without this, existing
  // saved plans (which only have the legacy field) render zeroed counts under the flag.
  const legacySelectedEmployeeIds = seat?.moduleSeat?.selectedEmployeeIds ?? [];
  const selectedInterviewersForStatus = (
    status: TrainingStatus,
    configSelectedEmployeeIds?: readonly string[] | null
  ) => {
    const selectedIds =
      configSelectedEmployeeIds !== undefined && configSelectedEmployeeIds !== null
        ? configSelectedEmployeeIds
        : legacySelectedEmployeeIds;
    return allInterviewModuleMembers.filter(
      (member) => member.status === status && selectedIds.includes(member.employeeId)
    );
  };

  const selectedTrainedInterviewers = selectedInterviewersForStatus(
    TrainingStatus.Trained,
    seat.moduleSeat?.trainedConfig?.selectedEmployeeIds
  );
  const selectedShadow = selectedInterviewersForStatus(
    TrainingStatus.Shadow,
    seat.moduleSeat?.shadowConfig?.selectedEmployeeIds
  );
  const selectedReverseShadow = selectedInterviewersForStatus(
    TrainingStatus.ReverseShadow,
    seat.moduleSeat?.reverseShadowConfig?.selectedEmployeeIds
  );
  const selectedTraineeInterviewers = [...selectedShadow, ...selectedReverseShadow];

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEdit();
  };

  const countAttributeValues = (map?: AttributeMap): number =>
    Object.values(map ?? {}).reduce((count, values) => count + (values?.length ?? 0), 0);

  const getInTrainingFilterLabel = ({
    roleAttributeMap,
    roleLabel,
    selectedCount,
  }: {
    roleAttributeMap?: AttributeMap;
    roleLabel: string;
    selectedCount: number;
  }): string | null => {
    if (selectedCount > 0) {
      return t('hiring-dashboard-filter:filter.rolePeopleFilterDetail', { role: roleLabel, count: selectedCount });
    }

    const filterCount = countAttributeValues(roleAttributeMap);
    return filterCount > 0
      ? t('hiring-dashboard-filter:filter.roleAttributesFilterDetail', { role: roleLabel, count: filterCount })
      : null;
  };

  if (!seat) {
    return null;
  }
  if (!seat.moduleSeat?.interviewModuleId) {
    return (
      <Typography variant="caption">
        {t('hiring-dashboard-interviewers:interviewers.plan.seat.noneSelected')}
      </Typography>
    );
  }

  if (!interviewModule) {
    return null;
  }

  const hasManuallySelectedTrainedInterviewers = selectedTrainedInterviewers.length > 0;
  const hasManuallySelectedInTrainingInterviewers = selectedTraineeInterviewers.length > 0;

  const hasAttributeFilters = Object.keys(attributeMap).length > 0;
  const traineeHasFilters = hasTraineeAttributeFilters({
    attributeMap,
    shadowAttributeMap,
    reverseShadowAttributeMap,
  });

  const filteredTrained = hasManuallySelectedTrainedInterviewers
    ? selectedTrainedInterviewers
    : filteredMembers.filter((member) => member.status === TrainingStatus.Trained);
  const filteredShadow = filteredMembers.filter((member) => member.status === TrainingStatus.Shadow);
  const filteredReverseShadow = filteredMembers.filter((member) => member.status === TrainingStatus.ReverseShadow);

  const interviewStats = statsData?.interviewModuleMemberStats?.filter(
    (stats) => stats?.interviewModule?.id === interviewModule?.id
  )?.[0];

  let shadowCount = 0;
  let reverseShadowCount = 0;
  if (hasManuallySelectedInTrainingInterviewers) {
    shadowCount = selectedShadow.length;
    reverseShadowCount = selectedReverseShadow.length;
  } else if (traineeHasFilters) {
    shadowCount = filteredShadow.length;
    reverseShadowCount = filteredReverseShadow.length;
  } else if (interviewStats) {
    shadowCount = Number(interviewStats.interviewModule?.membersInShadowCount || 0);
    reverseShadowCount = Number(interviewStats.interviewModule?.membersInReverseShadowCount || 0);
  }

  const trained = filteredTrained.length;

  const shadowFilterLabel = getInTrainingFilterLabel({
    roleLabel: t('hiring-dashboard-schedule:schedule.suggestedSchedule.shadow'),
    selectedCount: selectedShadow.length,
    roleAttributeMap: resolveInTrainingAttributeMap({
      role: 'SHADOW',
      attributeMap,
      shadowAttributeMap,
      reverseShadowAttributeMap,
    }),
  });
  const reverseShadowFilterLabel = getInTrainingFilterLabel({
    roleLabel: t('hiring-dashboard-schedule:schedule.suggestedSchedule.reverseShadow'),
    selectedCount: selectedReverseShadow.length,
    roleAttributeMap: resolveInTrainingAttributeMap({
      role: 'REVERSE_SHADOW',
      attributeMap,
      shadowAttributeMap,
      reverseShadowAttributeMap,
    }),
  });

  const trainedFilterDetail = (() => {
    if (hasManuallySelectedTrainedInterviewers) {
      return t('hiring-dashboard-filter:filter.peopleFilterDetail', { count: selectedTrainedInterviewers.length });
    }
    if (hasAttributeFilters) {
      return t('hiring-dashboard-filter:filter.attributesFilterDetail', { count: countAttributeValues(attributeMap) });
    }
    return null;
  })();

  const toTooltipMembers = (
    members: { employeeId: string; employee?: { fullName?: string | null } | null }[]
  ): TooltipMember[] =>
    members
      .map((member) => ({ employeeId: member.employeeId, fullName: member.employee?.fullName || '' }))
      .filter((member) => member.fullName);

  const trainedMembers = toTooltipMembers(filteredTrained);
  const shadowMembers = toTooltipMembers(hasManuallySelectedInTrainingInterviewers ? selectedShadow : filteredShadow);
  const reverseShadowMembers = toTooltipMembers(
    hasManuallySelectedInTrainingInterviewers ? selectedReverseShadow : filteredReverseShadow
  );

  const renderRoleTooltip = (
    header: string,
    detail: string | null,
    members: TooltipMember[],
    totalCount: number
  ): React.ReactNode => (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="caption" fontWeight="bold" display="block">
        {header}
      </Typography>
      {detail && (
        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
          {detail}
        </Typography>
      )}
      {members.slice(0, MAX_NAMES_PER_TOOLTIP).map((member) => (
        <Typography key={member.employeeId} variant="caption" display="block" sx={{ paddingLeft: 1 }}>
          {member.fullName}
        </Typography>
      ))}
      {totalCount > MAX_NAMES_PER_TOOLTIP && (
        <Typography variant="caption" display="block" sx={{ paddingLeft: 1, fontStyle: 'italic' }}>
          {t('hiring-dashboard-interviewers:interviewers.plan.seat.andMore', {
            count: totalCount - MAX_NAMES_PER_TOOLTIP,
          })}
        </Typography>
      )}
    </Box>
  );

  return (
    <Grid ref={rowRef}>
      <Box
        data-testid="module-seat-box-container"
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
        sx={sxProps.box}
        onClick={handleEdit}
      >
        <Grid container justifyContent="space-between" sx={gridSxProps.root} spacing={1} tabIndex={0} wrap="nowrap">
          <Grid style={{ flexGrow: 1 }}>
            <Grid container direction="column">
              <Grid container alignItems="center" spacing={1} style={{ width: '100%' }}>
                <Grid>
                  <Avatar sx={[avatarSxProps.root, trained === 0 && sxProps.errorChip]}>{seatIndex + 1}</Avatar>
                </Grid>
                <Grid>
                  <Typography>{t('hiring-dashboard-interviewers:interviewers.plan.seat.oneFrom')}</Typography>
                </Grid>
                <Grid style={{ overflow: 'hidden', maxWidth: '100%' }}>
                  <Chip
                    key={seat.moduleSeat.interviewModuleId}
                    sx={[
                      trained === 0 ? sxProps.errorChip : null,
                      !showOptions ? { border: `1px solid transparent` } : null,
                    ]}
                    label={<MaybeTooltip title={interviewModule.name} label={interviewModule.name} />}
                    avatar={<ModulesIcon />}
                    variant={showOptions ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid>
                  <Tooltip
                    title={renderRoleTooltip(
                      t('hiring-dashboard-interviewers:interviewers.plan.seat.filteredTrained', { count: trained }),
                      trainedFilterDetail,
                      trainedMembers,
                      trained
                    )}
                  >
                    <Stack
                      data-testid="module-seat-trained-count"
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ color: trained === 0 ? 'error.main' : 'text.secondary' }}
                    >
                      <DiamondFilled color="inherit" />
                      <Typography color="inherit">{trained}</Typography>
                      {trainedFilterDetail && <FilterIcon color="inherit" fontSize="small" />}
                    </Stack>
                  </Tooltip>
                </Grid>
              </Grid>
              {traineeRequirement !== TRAINEE_REQUIREMENT.NONE && (
                <Grid container alignItems="center" spacing={1} sx={sxProps.shadow}>
                  <Grid>
                    <TraineeIcon />
                  </Grid>
                  <Grid>
                    <Typography>{t('hiring-dashboard-interviewers:interviewers.plan.seat.training')}</Typography>
                  </Grid>
                  {traineeSchedulingStrategy === TRAINEE_SCHEDULING_STRATEGY.REQUIRED && (
                    <Grid>
                      <Chip
                        data-testid="module-seat-trainee-required-chip"
                        sx={[!showOptions ? { border: `1px solid transparent` } : null]}
                        label={t('hiring-dashboard-filter:filter.traineeRequirement.required')}
                        variant={showOptions ? 'outlined' : 'filled'}
                      />
                    </Grid>
                  )}
                  <Grid>
                    {/* Manual selections and attribute filters resolve counts client-side, so they
                        should render without waiting on the module-wide stats query. Only fall back
                        to the spinner when the counts genuinely depend on stats that are still loading. */}
                    {statsLoading && !hasManuallySelectedInTrainingInterviewers && !traineeHasFilters && (
                      <CircularProgress size={24} />
                    )}
                    {(hasManuallySelectedInTrainingInterviewers || traineeHasFilters || statsData) && (
                      <Stack
                        data-testid="module-seat-training-label"
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {showReverseShadowTrainee && (
                          <Tooltip
                            title={renderRoleTooltip(
                              t('hiring-dashboard-interviewers:interviewers.plan.seat.filteredReverseShadow', {
                                count: reverseShadowCount,
                              }),
                              reverseShadowFilterLabel,
                              reverseShadowMembers,
                              reverseShadowCount
                            )}
                          >
                            <Stack
                              data-testid="module-seat-reverse-shadow-count"
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ color: 'text.secondary' }}
                            >
                              <ReverseShadowIcon color="inherit" />
                              <Typography color="text.secondary">{reverseShadowCount}</Typography>
                              {reverseShadowFilterLabel && <FilterIcon color="inherit" fontSize="small" />}
                            </Stack>
                          </Tooltip>
                        )}
                        {showShadowTrainee && (
                          <Tooltip
                            title={renderRoleTooltip(
                              t('hiring-dashboard-interviewers:interviewers.plan.seat.filteredShadow', {
                                count: shadowCount,
                              }),
                              shadowFilterLabel,
                              shadowMembers,
                              shadowCount
                            )}
                          >
                            <Stack
                              data-testid="module-seat-shadow-count"
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ color: 'text.secondary' }}
                            >
                              <ShadowIcon color="inherit" />
                              <Typography color="text.secondary">{shadowCount}</Typography>
                              {shadowFilterLabel && <FilterIcon color="inherit" fontSize="small" />}
                            </Stack>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Attribute/people filtering lives in the inline edit-mode settings panel, so there is
              no hover filter button here. */}
          <Grid container justifyContent="flex-end" alignItems="center" xs wrap="nowrap" sx={sxProps.options}>
            <Grid>
              <Tooltip title={t('hiring-dashboard-interviewers:interviewers.about.edit')}>
                <IconButton
                  sx={sxProps.actionButton}
                  onClick={handleEdit}
                  size="small"
                  data-testid="module-seat-edit-icon-button"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid>
              <Tooltip title={t('common:shared.actions.delete')}>
                <IconButton
                  sx={sxProps.actionButton}
                  onClick={onDelete}
                  size="small"
                  data-testid="module-seat-delete-icon-button"
                >
                  <CrossIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

ModuleSeatV2.fragments = {
  seat: gql`
    fragment ModuleSeatV2_seat on JobStageInterviewSeat {
      id
      moduleSeat {
        interviewModuleId
        interviewModule {
          id
          orgId
          shadowsRequired
          reverseShadowsRequired
          membersCount
          name
          membersTrainedCount
          membersInTrainingCount
          membersInReverseShadowCount
          membersInShadowCount
          membersInPendingApprovalCount
          graduateFromShadowApprovalType
          graduateFromReverseShadowApprovalType
          interviewModuleMembers(input: { pageInput: { limit: 500, offset: 0 } }) {
            items {
              id
              interviewModuleId
              ...EmployeeFilter
            }
          }
        }
        selectedEmployeeIds
        jobStageInterviewSeatAttributes {
          id
          jobStageInterviewSeatId
          attributeNameId
          attributeTagValueId
        }
        trainedConfig {
          selectedEmployeeIds
          selectedAttributes {
            id
            jobStageInterviewSeatId
            attributeNameId
            attributeTagValueId
          }
        }
        shadowConfig {
          selectedEmployeeIds
          selectedAttributes {
            id
            jobStageInterviewSeatId
            attributeNameId
            attributeTagValueId
          }
        }
        reverseShadowConfig {
          selectedEmployeeIds
          selectedAttributes {
            id
            jobStageInterviewSeatId
            attributeNameId
            attributeTagValueId
          }
        }
        traineeRequirement
        traineeSchedulingStrategy
      }
    }
  `,
};

export default ModuleSeatV2;
